import { useState, useEffect, useCallback } from "react";

/**
 * Custom hook to manage modal states with proper focus management
 * Prevents multiple modals from interfering with each other
 */
export const useModalManager = () => {
  const [modals, setModals] = useState({});
  const [lastFocusedElement, setLastFocusedElement] = useState(null);

  // Store the focused element when a modal opens
  const openModal = useCallback(
    (modalKey, data = null) => {
      console.log("openModal called with:", modalKey, data);
      console.log("Current modals state:", modals);

      // Only store focus if we're not already in a modal
      if (!lastFocusedElement) {
        setLastFocusedElement(document.activeElement);
      }

      // Close all other modals first to prevent conflicts
      setModals((prev) => {
        const newModals = {};
        // Close all existing modals
        Object.keys(prev).forEach((key) => {
          newModals[key] = { isOpen: false, data: null };
        });
        // Open the requested modal
        newModals[modalKey] = { isOpen: true, data };
        console.log("Setting new modals state:", newModals);
        return newModals;
      });
    },
    [lastFocusedElement, modals]
  );

  // Close a specific modal
  const closeModal = useCallback(
    (modalKey) => {
      setModals((prev) => ({
        ...prev,
        [modalKey]: { isOpen: false, data: null },
      }));

      // Restore focus to the last focused element after a small delay
      setTimeout(() => {
        if (lastFocusedElement && lastFocusedElement.focus) {
          try {
            lastFocusedElement.focus();
          } catch (error) {
            // Fallback to body if element is no longer focusable
            document.body.focus();
          }
        }
        setLastFocusedElement(null);
      }, 100);
    },
    [lastFocusedElement]
  );

  // Close all modals
  const closeAllModals = useCallback(() => {
    setModals((prev) => {
      const newModals = {};
      Object.keys(prev).forEach((key) => {
        newModals[key] = { isOpen: false, data: null };
      });
      return newModals;
    });

    // Restore focus
    setTimeout(() => {
      if (lastFocusedElement && lastFocusedElement.focus) {
        try {
          lastFocusedElement.focus();
        } catch (error) {
          document.body.focus();
        }
      }
      setLastFocusedElement(null);
    }, 100);
  }, [lastFocusedElement]);

  // Switch from one modal to another with proper cleanup
  const switchModal = useCallback((fromModalKey, toModalKey, data = null) => {
    setModals((prev) => ({
      ...prev,
      [fromModalKey]: { isOpen: false, data: null },
      [toModalKey]: { isOpen: false, data }, // Set to false initially
    }));

    // Open the new modal after a delay to ensure proper cleanup
    setTimeout(() => {
      setModals((prev) => ({
        ...prev,
        [toModalKey]: { isOpen: true, data },
      }));
    }, 150);
  }, []);

  // Get modal state
  const getModalState = useCallback(
    (modalKey) => {
      const state = modals[modalKey] || { isOpen: false, data: null };
      console.log(`getModalState(${modalKey}):`, state);
      return state;
    },
    [modals]
  );

  // Check if any modal is open
  const isAnyModalOpen = useCallback(() => {
    return Object.values(modals).some((modal) => modal.isOpen);
  }, [modals]);

  // Initialize modal state
  const initModal = useCallback(
    (modalKey) => {
      if (!modals[modalKey]) {
        setModals((prev) => ({
          ...prev,
          [modalKey]: { isOpen: false, data: null },
        }));
      }
    },
    [modals]
  );

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      if (lastFocusedElement && lastFocusedElement.focus) {
        try {
          lastFocusedElement.focus();
        } catch (error) {
          // Ignore cleanup errors
        }
      }
    };
  }, [lastFocusedElement]);

  return {
    openModal,
    closeModal,
    closeAllModals,
    switchModal,
    getModalState,
    isAnyModalOpen,
    initModal,
  };
};

export default useModalManager;
